import { call, put, takeEvery } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import request, { type RequestError } from "../../../utils/request";
import { API_ENDPOINT } from "../../../utils/constants";
import { handleAxiosError } from "../../../utils/APIValidations";
import { formatUkDate, formatUkTimeOfDay } from "../../../utils/datetime";
import {
  loadSummary,
  summaryLoaded,
  loadSummaryError,
  loadCalls,
  callsLoaded,
  loadCallsError,
  loadCallDetails,
  callDetailsLoaded,
  loadCallDetailsError,
} from "./slice";
import type {
  AllCallsResponse,
  ApiCallRecord,
  CallDetailsResponse,
  DashboardSummary,
  LoadCallDetailsPayload,
  LoadCallsPayload,
  RecentCall,
} from "./types";

const EMPTY_SUMMARY: DashboardSummary = {
  total_calls: 0,
  successful_calls: 0,
  unsuccessful_calls: 0,
  follow_up_calls: 0,
  success_rate: 0,
};

function isNoData(e: unknown): boolean {
  return (e as RequestError)?.status === 404;
}

function mapRecord(r: ApiCallRecord): RecentCall {
  return {
    key: r.call_id,
    callId: r.call_id,
    callerName: r.caller || "Unknown",
    callerPhone: r.phone_number || "—",
    purpose: r.purpose ?? "",
    duration: r.duration ?? "—",
    status: r.successful ? "Successful" : "Unsuccessful",
    date: formatUkDate(r.created_at),
    time: formatUkTimeOfDay(r.created_at),
    recordingUrl: r.recording_url ?? null,
    followUp: !!r.follow_up,
    isDone: !!r.is_done,
  };
}

export function* fetchSummary() {
  try {
    const response: DashboardSummary = yield call(
      request,
      `${API_ENDPOINT}dashboard/calls`,
    );
    yield put(summaryLoaded(response));
  } catch (e) {
    if (isNoData(e)) {
      yield put(summaryLoaded(EMPTY_SUMMARY));
      return;
    }
    yield call(handleAxiosError, e);
    yield put(loadSummaryError());
  }
}

export function* fetchCalls({
  payload,
}: PayloadAction<LoadCallsPayload | undefined>) {
  try {
    const params = new URLSearchParams({ page: "1", per_page: "1000" });
    if (payload?.startDate) params.set("start_date", payload.startDate);
    if (payload?.endDate) params.set("end_date", payload.endDate);

    const response: AllCallsResponse = yield call(
      request,
      `${API_ENDPOINT}dashboard/all-calls?${params.toString()}`,
    );
    yield put(callsLoaded(response.records.map(mapRecord)));
  } catch (e) {
    if (isNoData(e)) {
      yield put(callsLoaded([]));
      return;
    }
    yield call(handleAxiosError, e);
    yield put(loadCallsError());
  }
}

export function* fetchCallDetails({
  payload,
}: PayloadAction<LoadCallDetailsPayload>) {
  try {
    const response: CallDetailsResponse = yield call(
      request,
      `${API_ENDPOINT}dashboard/call-details?call_id=${encodeURIComponent(
        payload.callId,
      )}`,
    );
    yield put(callDetailsLoaded(response));
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(loadCallDetailsError());
  }
}

export default function* saga() {
  yield takeEvery(loadSummary.type, fetchSummary);
  yield takeEvery(loadCalls.type, fetchCalls);
  yield takeEvery(loadCallDetails.type, fetchCallDetails);
}
